import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

const EstimatedTotalSection = ({ bookingTotalModel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const detailsRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  // Refs for responsive estimated-total value sizing
  const containerRef = useRef(null);
  const leftRef = useRef(null);
  const valueRef = useRef(null);


  function formatWorkTime(workTime) {
    const cleanWorkTime = workTime.replace(" hours", "");

    const format = (decimalHour) => {
      const totalMinutes = Math.round(Math.abs(decimalHour) * 60);

      if (totalMinutes < 60) {
        return `${totalMinutes.toString().padStart(2, "0")} min`;
      }

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (minutes === 0) return `${hours} hr`;

      return `${hours} hr ${minutes.toString().padStart(2, "0")} min`;
    };

    const values = cleanWorkTime
      .split("-")
      .filter(v => v.trim() !== "")
      .map(Number);

    if (values.length === 1) {
      return format(values[0]);
    }

    return `${format(values[0])} - ${format(values[1])}`;
  }

  // Format currency or currency range into readable non-wrapping string
  const formatCurrencyRange = (value) => {
    if (value === null || typeof value === 'undefined' || value === '') return '-';

    const formatNumber = (raw) => {
      const num = typeof raw === 'number'
        ? raw
        : parseFloat(String(raw).replace(/[^0-9.\-]/g, ''));
      if (Number.isNaN(num)) return null;
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const str = String(value);
    // split on hyphens (normal, en-dash, em-dash)
    const parts = str.split(/[-–—]/).map(p => p.trim()).filter(Boolean);

    if (parts.length === 0) return '-';
    if (parts.length === 1) {
      const f = formatNumber(parts[0]);
      return f ? <span className="whitespace-nowrap">${f}</span> : '-';
    }

    const left = formatNumber(parts[0]);
    const right = formatNumber(parts[1]);
    if (left && right) {
      return (
        <span className="whitespace-nowrap">
          ${left} <span className="mx-1">–</span> ${right}
        </span>
      );
    }

    // Fallback: show original value but prevent wrapping
    return <span className="whitespace-nowrap">{str}</span>;
  };


  const toggleOpen = () => setIsOpen((s) => !s);

  useEffect(() => {
    if (!detailsRef.current) return;
    if (isOpen) setMaxHeight(`${detailsRef.current.scrollHeight}px`);
    else setMaxHeight('0px');
  }, [isOpen]);

  // Adjust the font-size of the total value so it fits on one line
  useLayoutEffect(() => {
    const adjust = () => {
      if (!containerRef.current || !leftRef.current || !valueRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const leftWidth = leftRef.current.clientWidth;
      // small gap allowance between label and value
      const available = Math.max(40, containerWidth - leftWidth - 24);

      // constrain value container width
      valueRef.current.style.maxWidth = `${available}px`;
      valueRef.current.style.whiteSpace = 'nowrap';
      valueRef.current.style.overflow = 'hidden';

      const maxFs = 20;
      const minFs = 12;
      let fs = maxFs;
      valueRef.current.style.fontSize = `${fs}px`;

      // reduce font-size until it fits or reach minimum
      while (valueRef.current.scrollWidth > valueRef.current.clientWidth && fs > minFs) {
        fs -= 1;
        valueRef.current.style.fontSize = `${fs}px`;
      }
    };

    adjust();
    window.addEventListener('resize', adjust);
    return () => window.removeEventListener('resize', adjust);
  }, [bookingTotalModel?.totalCharge, bookingTotalModel?.subtotal]);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen && detailsRef.current) setMaxHeight(`${detailsRef.current.scrollHeight}px`);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  return (
    <>
      {/* Estimated total */}
      <div ref={containerRef} className="flex items-center justify-between bg-transparent mb-[19px]">
        <div ref={leftRef} className="flex items-center gap-2">
          <img src="/images/NewUiIcons/SummaryIcons.svg" alt="dollar icon" width={18} />
          <div className="text-[#6B688E] font-bold text-[16px]">Estimated total:</div>
        </div>
        <div ref={valueRef} className="text-[#211F59] text-[20px] font-bold" style={{ fontSize: '20px' }}>{formatCurrencyRange(bookingTotalModel?.totalCharge)}</div>
      </div>
      <div>
        <div className="flex items-center gap-[7.5px]">
          <button onClick={toggleOpen} aria-expanded={isOpen} className="flex items-center focus:outline-none">
            <img
              src="/Vision Agent Icons/Back Arrow/ChevronLightPurple.svg"
              alt={isOpen ? 'collapse details' : 'expand details'}
              className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </button>

          <span
            onClick={toggleOpen} aria-expanded={isOpen}
           className="font-medium text-[12px] text-[#7876A1] cursor-pointer">{isOpen ? 'Hide details' : 'Show details'}</span>
          <hr className="h-[1px] flex-1 bg-[#CDCCD7]" />
        </div>

        <div ref={detailsRef} className={`${isOpen ? 'h-auto' : 'h-0'} overflow-hidden transition-all duration-300 `} style={{ maxHeight }}>
          <div className="pl-[90px]">
            <div className="text-[#7876A1] text-[14px] flex justify-between py-2 ">
              <div className="font-medium">Work time:</div>
              <div className="font-normal text-[#211F59]">{formatWorkTime(bookingTotalModel?.workTime || 'N/A')}</div>
            </div>

            <div className="text-[#7876A1] text-[14px] flex justify-between py-2 ">
              <div className="font-medium">Travel time:</div>
              <div className="font-normal text-[#211F59]">{formatWorkTime(bookingTotalModel?.travelTime || 'N/A')}</div>
            </div>

            {
              bookingTotalModel?.hourlyRate && (
                <div className="text-[#7876A1] text-[14px] flex justify-between py-2 ">
                  <div className="font-medium">Hourly rate:</div>
                  <div className="font-normal text-[#211F59]">${bookingTotalModel?.hourlyRate || 'N/A'}</div>
                </div>
              )
            }


            <div className="text-[#7876A1] text-[14px] flex justify-between py-2 ">
              <div className="font-medium">Booking fee:</div>
              <div className="font-normal text-[#211F59]">${bookingTotalModel?.totalServiceFee || 'N/A'}</div>
            </div>

            <hr className="border-[1px] border-dashed border-[#C8C6DF] my-2" />

            <div className="text-[14px] flex justify-between py-2">
              <div className="font-semibold text-[#7876A1]">Subtotal:</div>
              <div className="font-normal text-[#211F59]">${bookingTotalModel?.subtotal || 'N/A'}</div>
            </div>

            <div className="text-[14px] flex justify-between py-2">
              <div className="font-semibold text-[#7876A1]">GST:</div>
              <div className="font-normal text-[#211F59]">${bookingTotalModel?.gstandPst || 'N/A'}</div>
            </div>


            {
              bookingTotalModel?.discount !== 0 && bookingTotalModel?.discount !== null && (
                <div className="text-[14px] flex justify-between py-2">
                  <div className="font-semibold text-[#7876A1]">Discount:</div>
                  <div className="font-normal text-[#211F59]">- ${bookingTotalModel?.discount || '0'}</div>
                </div>
              )
            }


          </div>

          <p className="text-[#7876A1] font-normal text-[12px] mt-[12.5px]">
          *Rates are calculated hourly based on the truck and crew required. Quotes may include stairs, elevator use, and (dis)assembly, and assume up to 50 m carry. Extra time is billed at the hourly rate shown.
          </p>

          <div
            style={{
              height: "1px",
              backgroundColor: "#7876A1",
              marginTop: "18.63px",
            }}
          />
        </div>
      </div>
    </>
  );
};

export default EstimatedTotalSection;
